/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react'

// Images
import Chevron from 'img/chevron.inline.svg'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Third Party
import styled from 'styled-components'

interface FAQProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Faq
}

const FaqSection = styled.section`
  background-color: ${({ theme }) => theme.color.grey};
  padding: 30px;
  margin-bottom: 1.5rem;

  @media screen and (max-width: 991px) {
    padding: 20px;
  }
`

const Content = styled(ParseContent)`
  margin-bottom: 1.5rem;
`

const FaqItem = styled.div`
  border-bottom: 1px solid rgba(28, 28, 28, 0.12);

  &:first-child {
    border-top: 1px solid rgba(28, 28, 28, 0.12);
  }
`

const QuestionButton = styled.button<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.medium};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ isOpen, theme }) =>
    isOpen ? theme.color.primary : theme.color.dark};

  & svg {
    flex-shrink: 0;
    margin-left: 20px;
    transition: transform 0.3s ease;

    path {
      fill: ${({ isOpen, theme }) =>
        isOpen ? theme.color.primary : theme.color.dark};
    }
  }

  &:hover {
    color: ${({ theme }) => theme.color.primary};

    & svg path {
      fill: ${({ theme }) => theme.color.primary};
    }
  }
`

const ChevronIcon = styled(Chevron)<{ isOpen: boolean }>`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`

const AnswerWrapper = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '800px' : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: max-height 0.3s ease, opacity 0.3s ease;
`

const AnswerContent = styled.div`
  padding-bottom: 20px;
`

const FAQ: React.FC<FAQProps> = ({ fields }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  if (!fields.item?.length) {
    return null
  }

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <FaqSection>
      {fields.title && <Content content={fields.title} />}
      {fields.item.map((item, index) => {
        const isOpen = openIndex === index

        return (
          <FaqItem key={index}>
            <QuestionButton
              type="button"
              isOpen={isOpen}
              onClick={() => toggleItem(index)}
              aria-expanded={isOpen}
            >
              {item?.question}
              <ChevronIcon isOpen={isOpen} />
            </QuestionButton>
            <AnswerWrapper $open={isOpen}>
              <AnswerContent>
                <ParseContent content={item?.answer || ''} />
              </AnswerContent>
            </AnswerWrapper>
          </FaqItem>
        )
      })}
    </FaqSection>
  )
}

export default FAQ
